<template>
  <Progressbar v-if="loading" />
  <NetworkError v-else-if="!loading && errors" @tryAgain="getData" />
  <div class="w-full h-full bg-gray-200 dark:bg-base-100 rounded-tr-3xl xl:px-9 pt-6 lg:pt-10 pb-14" v-else>
    <div class="container">
      <div>
        <div class="grid md:grid-cols-4 grid-cols-2 gap-4 mb-6">
          <div v-for="metric in meta.metrics" :key="metric.key"
            class="flex items-start dark:bg-base-300 bg-white px-4 py-7 rounded-lg">
            <div class="w-10 h-10 ml-3 rounded-xl flex items-center justify-center" :class="colorMap[metric.key]">
              <component v-if="iconMap[metric.key]" :is="iconMap[metric.key]" class="w-5 h-5 text-white" />
            </div>

            <div class="flex flex-col">
              <span class="text-gray-400 text-xs mb-1">
                {{ metric.title }}
              </span>

              <span class="text-gray-800 dark:text-white text-xl font-bold">
                {{ metric.value }}
              </span>
            </div>
          </div>
        </div>

        <div class="mb-12 grid grid-cols-24 items-center sm:gap-5">
          <div
            class="grid grid-cols-2 xl:grid-cols-4 gap-4 dark:bg-base-300 bg-white rounded-lg xl:p-6 sm:p-4 p-2 col-span-full">
            <select v-model="query.user_id" class="select" name="user_id" v-if="type == 'product-sale'">
              <option value="">{{ __("All users") }}</option>
              <option v-for="item in meta?.users" :value="item.id">
                {{ item.name }}
              </option>
            </select>
            <select v-if="['lead-source','campaign'].includes(type)" v-model="query.model" class="select" name="model">
              <option value="person">{{ __("Persons") }}</option>
              <option value="clue">{{ __("Clues") }}</option>
              <option value="opportunity" selected>{{ __("Opportunities") }}</option>
            </select>
            <select v-if="type == 'failure-reason'" v-model="query.model_id" class="select" name="model_id">
              <option value="">{{ __("All failure reasons") }}</option>
              <option v-for="item in meta?.failure_reasons" :value="item.id">
                {{ item.title }}
              </option>
            </select>
            <select v-else-if="type == 'lead-source'" v-model="query.model_id" class="select" name="model_id">
              <option value="">{{ __("All lead sources") }}</option>
              <option v-for="item in meta?.lead_sources" :value="item.id">
                {{ item.title }}
              </option>
            </select>
            <select v-else-if="type == 'campaign'" v-model="query.model_id" class="select" name="model_id">
              <option value="">{{ __("All campaigns") }}</option>
              <option v-for="item in meta?.campaigns" :value="item.id">
                {{ item.name }}
              </option>
            </select>
            <select v-else-if="type == 'product-sale'" v-model="query.model_id" class="select" name="model_id">
              <option value="">{{ __("All products") }}</option>
              <option v-for="item in meta?.products" :value="item.id">
                {{ item.title }}
              </option>
            </select>
            <select v-model="query.period" class="select" name="period">
              <option value="week">{{ __("Weekly") }}</option>
              <option value="month" selected>{{ __("Monthly") }}</option>
              <option value="year">{{ __("Annually") }}</option>
            </select>
            <select v-model="query.metric" class="select" name="metric">
              <option value="count">{{ __("Count") }}</option>
              <option value="amount" :disabled="query.model != 'opportunity'">{{ __("Amount") }}</option>
            </select>
            <!-- <DatePicker v-model="query.date" :placeholder="__('Date Range')" inputClass="input" range clearable /> -->
          </div>
          <div class="dark:bg-base-300 bg-white rounded-lg xl:p-6 sm:p-4 p-2 col-span-full" v-for="chart in statistics">
            <h2 class="text-dark-550 dark:text-white font-bold flex items-center text-xl">
              <i class="w-2 h-2 bg-gray-350 dark:bg-white ml-2 rounded-full"></i>
              {{ chart.title }}
            </h2>
            <div class="divider col-span-full mt-0 mb-3"></div>
            <div class="min-h-[24rem]">
              <BarChart :options="chart" :stacked="chart.stacked" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>

const { getStatistics, statistics, router, route, meta, loading, errors } = useDashboard();
import { useHead } from "@vueuse/head";
import { watch } from "vue";
import {
  Squares2X2Icon,
  BoltIcon,
  TrophyIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
} from '@heroicons/vue/24/outline'
const iconMap = {
  total: Squares2X2Icon,
  active_total: BoltIcon,
  top_count: TrophyIcon,
  top_income: BanknotesIcon,
  sale_day: ShoppingCartIcon,
  top_day: StarIcon,
  sale_week: ArrowTrendingUpIcon,
  top_week: CalendarDaysIcon,
}
const colorMap = {
  total: 'bg-slate-600',
  active_total: 'bg-blue-600',
  top_count: 'bg-purple-600',
  top_income: 'bg-green-700',
  sale_day: 'bg-green-600',
  top_day: 'bg-purple-600',
  sale_week: 'bg-blue-600',
  top_week: 'bg-cyan-600'
}

const buildQuery = (initQuery, currentQuery) => {
  return Object.keys(currentQuery).reduce((result, key) => {
    if (
      currentQuery[key] !== initQuery[key] &&
      currentQuery[key] !== "" &&
      currentQuery[key] !== null &&
      currentQuery[key] !== undefined
    ) {
      result[key] = currentQuery[key];
    }
    return result;
  }, {});
}
const initQuery = {
  period: "week",
  user_id: "",
  model_id: "",
  model: "opportunity",
  metric: "count",
  date: ""
};
const type = ref('')
const query = reactive({ ...initQuery, ...route.query });

const getData = (newType = type.value, query = {}) => {
  type.value = newType
  return getStatistics(newType, query);
};
useHead({
  title: window.i18n["Statictic " + route.params.type.replace("-", " ") + "s"],
})
onMounted(() => {
  getData(route.params.type, route.query);
});

watch(query, (value) => {
  if (value.model != 'opportunity' && value.metric == 'amount') query.metric = 'count';
  getData(route.params.type, window.setQueries(router, buildQuery(initQuery, value)));
});

watch(
  () => route.params.type,
  (newType) => {
    Object.assign(query,initQuery);
    getData(newType);
  }
);
</script>
